import { useEffect, useId, useState, type ReactNode } from 'react';
import { useRouter } from 'next/router';
import { ChevronDown, MoreHorizontal } from 'lucide-react';
import { Avatar, Badge, Button, Card, Collapsible, IconButton, Tabs, TextButton, Typography } from '../ui';
import { avatarSlotFor } from '../../../lib/avatar-color';
import { useTranslation } from '../../../lib/i18n';
import { meetingClock, meetingDayHeading } from '../../../lib/meeting-time';
import { attendeeInitials, meetingWhen } from '../../../lib/meeting-when';
import { useGenericBi } from '../../../lib/use-generic-bi';
import { useAgentName } from '../../../lib/use-agent-name';
import { useLoadBi } from '../../../lib/use-load-bi';
import {
  groupByDay,
  MEETING_STATE_BADGE,
  meetingRowAction,
  meetingRowDecision,
  meetingRowKey,
  meetingsNotEnded,
  openMeetingEventThread,
  splitNextMeeting,
  useMeetingsChannelActions,
  useMeetingsSchedule,
  type UpcomingMeeting,
} from '../../../lib/meetings-channel';
import { MeetingHistorySection } from './MeetingHistorySection';
import { MeetingsCalendarModal } from './MeetingsCalendarModal';
import { MeetingsSettingsModal } from './MeetingsSettingsModal';
import styles from './MeetingsChannelSection.module.css';

const CHANNELS_ROUTE = 'settings-connectors';

/**
 * How many meetings the Upcoming tab shows before "Show N more".
 *
 * The cap hides rows, never data: every video event in the next seven days stays one tap away, and the
 * button says how many are behind it. A silently truncated week would make the card answer "what will
 * she do this week" with a half-answer that looks complete — and the row a user most needs to Skip is
 * as likely to be the eighth as the first. Same number on both phones.
 */
const VISIBLE_MEETINGS = 4;
const MEETING_CLOCK_TICK_MS = 30_000;

type MeetingsTab = 'upcoming' | 'past';

/**
 * The Meetings channel: whether Maestro may join this site's video calls, what she will do in the next
 * seven days, and the meetings she has already been in. Meetings is a connection, not an agent, so this
 * view is the only place any of it lives.
 *
 * **Connected, it opens folded to one summary** — the next meeting and how many are ahead — because the
 * whole view is taller than every other channel on the tab put together. It opens expanded when the page
 * was deep-linked to it, and after a first connect. Expanded, the next meeting leads in its own card and
 * the rest sit under Upcoming / Past tabs; the connection's settings (Reconnect, Disconnect, the two
 * switches) open in a dialog from the ⋯. iOS and Android draw the same view; there the settings are a sheet.
 *
 * **A connection is the consent, and no row is "never".** The gate behind this card refuses any site
 * without a connection row, so pressing Connect is what switches the feature on at all. Connect asks for
 * `REQUESTED_ONLY`; the user turns automatic joining on separately, with the switch here.
 *
 * **Every connect goes through the calendar modal.** "Connect a calendar" opens it whether or not a
 * calendar is already there: a row's Connect adds one and finishes the connection on its own, and the
 * footer Connect connects to the calendar the site already has. Reconnect opens the same modal.
 *
 * **Nothing in the list is stored.** Events are read from the calendar live and each row's state
 * comes from the same gate the join capsule runs, so the page cannot promise a join the capsule
 * would refuse.
 */
export function MeetingsChannelSection({
  title,
  about,
  headClassName,
  bodyInCard = false,
  defaultExpanded = false,
}: {
  /**
   * The channel's name and its one line, as the wrapping page styles them. Passed in rather than drawn
   * by the page so the connected state can share the title's ROW — "Meetings", then its Connected badge,
   * the ⋯ and the chevron at the row's end — which the page cannot do without knowing the connection.
   */
  title?: ReactNode;
  about?: ReactNode;
  /** The page's own head block, when it wraps title and line in one (the phone layout does). */
  headClassName?: string;
  /**
   * Frame the body in the channel card. The desktop page already sets the whole section inside one card;
   * the phone layout draws its head on the page and needs the body framed here instead.
   */
  bodyInCard?: boolean;
  /** Start expanded — the page was deep-linked to this section. */
  defaultExpanded?: boolean;
} = {}) {
  // The section's head when the page handed over its title: the title, then (once connected) the badge,
  // the ⋯ and the chevron at the row's end, then the page's one line under it.
  const framed = (body: ReactNode, trailing?: ReactNode) => {
    if (title === undefined) return body;
    const head = (
      <>
        <div className={styles.titleRow}>
          <span className={styles.titleLead}>{title}</span>
          {trailing && <span className={styles.titleTrail}>{trailing}</span>}
        </div>
        {about}
      </>
    );
    return (
      <>
        {headClassName ? <div className={headClassName}>{head}</div> : head}
        {body}
      </>
    );
  };
  const { t, i18n } = useTranslation();
  const { logPageClickBi } = useGenericBi();
  const { schedule, loading, error } = useMeetingsSchedule();
  const ownAgent = useAgentName();
  const { connect, update, disconnect, decide, refresh } = useMeetingsChannelActions();
  useLoadBi('meetings-schedule-data', { isLoading: loading, isError: error });
  const router = useRouter();
  const bodyId = useId();
  const [expanded, setExpanded] = useState(defaultExpanded);
  // A deep link can arrive while the page is already mounted (client-side navigation, query hydration), so
  // the state follows the target when it turns on. It never folds the section back, which would undo the
  // user's own toggle.
  useEffect(() => {
    if (defaultExpanded) setExpanded(true);
  }, [defaultExpanded]);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [tab, setTab] = useState<MeetingsTab>('upcoming');
  const [openingKey, setOpeningKey] = useState<string | null>(null);
  const [openFailed, setOpenFailed] = useState(false);
  const [calendarModalOpen, setCalendarModalOpen] = useState(false);
  // Set once the modal's Google connect succeeds. The schedule read can lag behind the new calendar, and
  // if the meeting connect then fails, the modal's footer Connect must still be offered on a retry.
  // Cleared once the connect lands or the user disconnects, so a stale "a calendar just arrived" can never
  // offer a connect to a calendar that has since gone.
  const [calendarJustConnected, setCalendarJustConnected] = useState(false);
  // "In 10 minutes" has to become "Starting now" while the page sits open, so the view re-reads the clock
  // on a short tick rather than only when the schedule refetches.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const tick = setInterval(() => setNow(Date.now()), MEETING_CLOCK_TICK_MS);
    return () => clearInterval(tick);
  }, []);

  const clicked = (button_name: string) => logPageClickBi({ page_name: CHANNELS_ROUTE, button_name });

  // A meeting's title opens its conversation: created on the first open, and the meeting's own thread
  // from then on, so it is where its brief is set and where its write-up lands. The same query param
  // every conversation link in the app uses.
  const openMeeting = async (meeting: UpcomingMeeting) => {
    clicked('meetings_row_open');
    setOpeningKey(meetingRowKey(meeting));
    setOpenFailed(false);
    try {
      const conversationId = await openMeetingEventThread(meeting);
      await router.push({ pathname: '/', query: { conversation: conversationId, chat: 'expanded' } });
    } catch {
      setOpenFailed(true);
    } finally {
      setOpeningKey(null);
    }
  };

  if (loading) {
    return framed(
      <Card variant="stone" size="sm" className={styles.card}>
        <Typography variant="p2" color="secondary">
          {t('connectors.meetings.loading')}
        </Typography>
      </Card>,
    );
  }

  if (error || !schedule) {
    return framed(
      <Card variant="stone" size="sm" className={styles.card}>
        <Typography variant="p2" color="secondary">
          {t('connectors.meetings.error')}
        </Typography>
      </Card>,
    );
  }

  const { calendars, canTalkAvailable, connection, joiningAllowedForSite, meetings } = schedule;
  // The owner's own agent name, for "{agent} will join": in the room she is their agent, not a separate one.
  const agentName = ownAgent.short.trim() || undefined;
  // A first connect lands the reader on a view they have not seen yet, so it opens rather than folding
  // everything they just set up behind a chevron.
  const connectMeetings = () =>
    connect.mutate(undefined, {
      onSuccess: () => {
        setCalendarJustConnected(false);
        setExpanded(true);
      },
    });
  const inCard = (node: ReactNode) =>
    bodyInCard ? (
      <Card variant="stone" size="sm" className={styles.cardFrame}>
        {node}
      </Card>
    ) : (
      node
    );
  const emails = calendars
    .map((calendar) => calendar.email)
    .filter(Boolean)
    .join(', ');

  // One modal for the first connect and for Reconnect. A calendar that lands through it finishes the
  // flow on its own — but only a FIRST connect creates the connection. On Reconnect the connection exists,
  // and posting the first-connect body would reset automatic joining to REQUESTED_ONLY behind the user's
  // back, so it only re-reads the schedule.
  const calendarModal = calendarModalOpen && (
    <MeetingsCalendarModal
      hasCalendar={calendars.length > 0 || calendarJustConnected}
      connected={Boolean(connection)}
      connecting={connect.isLoading}
      onClose={() => setCalendarModalOpen(false)}
      onCalendarConnected={() => {
        setCalendarJustConnected(true);
        setCalendarModalOpen(false);
        if (connection) {
          refresh();
        } else {
          connectMeetings();
        }
      }}
      onConnect={() => {
        setCalendarModalOpen(false);
        connectMeetings();
      }}
    />
  );

  if (!connection) {
    return framed(
      /* Just the action. The section card that wraps this one already carries the channel's name and the
         line saying what it does, so anything more here would repeat it. */
      inCard(
        <div className={styles.connect}>
          {/* The allowlist notice, in front of the decision rather than after it. Connect stays ENABLED
            under it: connecting writes the row, and the connect backfill arms her meetings the moment
            the allowlist opens. Disabling it would hide a feature behind a config value the user cannot
            see or change. */}
          {!joiningAllowedForSite && (
            <Typography variant="p2" color="secondary" className={styles.notice}>
              {t('connectors.meetings.notEnabled.beforeConnect')}
            </Typography>
          )}
          <Button
            size="small"
            disabled={connect.isLoading}
            loading={connect.isLoading}
            onClick={() => {
              clicked('meetings_connect');
              setCalendarModalOpen(true);
            }}
          >
            {t('connectors.meetings.needCalendar.cta')}
          </Button>
          {connect.isError && (
            <Typography variant="p3" color="secondary" component="p" role="alert">
              {t('connectors.meetings.connectFailed')}
            </Typography>
          )}
          {calendarModal}
        </div>,
      ),
    );
  }

  const busy = update.isLoading || disconnect.isLoading;
  // Re-filtered on the view's own clock: a meeting that ends while the page is open leaves the list.
  const upcoming = meetingsNotEnded(meetings, now);
  const { next, rest } = splitNextMeeting(upcoming);
  const locale = i18n.language || undefined;
  const whenLabel = (meeting: UpcomingMeeting) => {
    const when = meetingWhen(meeting.startsAt, now, i18n.language || undefined);
    return when.kind === 'startingNow' ? t('connectors.meetings.startingNow') : when.text;
  };
  const stateLabel = (meeting: UpcomingMeeting) =>
    meeting.state === 'scheduled' && agentName
      ? t('connectors.meetings.state.scheduledBy', { agent: agentName })
      : t(`connectors.meetings.state.${meeting.state}`);
  const attendeesLabel = (meeting: UpcomingMeeting) =>
    meeting.participantCount <= 0
      ? ''
      : meeting.participantCount === 1
      ? t('connectors.meetings.attendeesOne')
      : t('connectors.meetings.attendees', { count: meeting.participantCount });
  const onDecide = (meeting: UpcomingMeeting, decision: 'join' | 'skip' | null) => {
    clicked(`meetings_row_${decision ?? 'clear'}`);
    decide.mutate({ meeting, decision });
  };

  const toggleExpanded = () => {
    clicked(expanded ? 'meetings_collapse' : 'meetings_expand');
    setExpanded(!expanded);
  };
  // The ⋯ opens the settings dialog from either state; the dialog does not depend on the section being open.
  const openSettings = () => {
    clicked('meetings_settings');
    setSettingsOpen(true);
  };

  const trailing = (
    <>
      <Badge variant="connected" label={t('common.connected')} />
      <IconButton
        size="small"
        variant="text"
        aria-label={t('connectors.meetings.settings')}
        aria-haspopup="dialog"
        onClick={openSettings}
      >
        <MoreHorizontal size={16} aria-hidden />
      </IconButton>
      <IconButton
        size="small"
        variant="text"
        className={[styles.chevron, expanded ? styles.chevronOpen : ''].filter(Boolean).join(' ')}
        aria-label={t(expanded ? 'connectors.meetings.collapse' : 'connectors.meetings.expand')}
        aria-expanded={expanded}
        aria-controls={bodyId}
        onClick={toggleExpanded}
      >
        <ChevronDown size={16} aria-hidden />
      </IconButton>
    </>
  );

  /**
   * The switches go dead while the site is not switched on, for the same reason a `paused` row draws no
   * Join: neither control can reach the thing standing in the way. The notice above them is the
   * explanation — a live switch with a line of text under it saying it will not work reads as a bug, and
   * one that quietly persists a preference nothing will honour is worse than one that is plainly
   * unavailable.
   */
  const switchesLocked = busy || !joiningAllowedForSite;

  // Counts what is still ahead: a meeting her session already dropped out of is not.
  const ahead = upcoming.filter((meeting) => meeting.state !== 'failed').length;
  const upcomingCount = ahead === 1 ? t('connectors.meetings.upcomingCountOne') : t('connectors.meetings.upcomingCount', { count: ahead });

  return framed(
    inCard(
      <div className={styles.channelConnected}>
        {/* Without a page title to share (a caller that passes none), the badge and the controls keep a
          row of their own rather than disappearing. */}
        {title === undefined && <div className={styles.channelHead}>{trailing}</div>}
        {/* The site is connected but the product switch is off for it. Said plainly, and in both states,
            because "you turned her on" and "we have turned this on for you" are different facts and only
            the first is the user's to change — without this the switches read as broken. */}
        {!joiningAllowedForSite && (
          <Typography variant="p2" color="secondary" className={styles.notice}>
            {t('connectors.meetings.notEnabled')}
          </Typography>
        )}

        {!expanded && (
          <Card
            component="button"
            variant={bodyInCard ? 'sunken' : 'stone'}
            size="sm"
            interactive
            className={styles.summary}
            aria-controls={bodyId}
            aria-expanded={false}
            onClick={() => {
              clicked('meetings_expand');
              setExpanded(true);
            }}
          >
            {/* Three lines, no side column, so a long title and a long status never compete for one row's width. */}
            {next ? (
              <>
                <Typography variant="p3" color="secondary" component="span" className={styles.nextUpLabel}>
                  {`${t('connectors.meetings.nextUp')} · ${whenLabel(next)}`}
                </Typography>
                <Typography variant="p1" color="primary" component="span" className={styles.summaryTitle}>
                  {next.title || t('connectors.meetings.untitled')}
                </Typography>
                <span className={styles.summaryLine}>
                  <Typography variant="p3" color="secondary" component="span" className={styles.summaryMeta}>
                    {[stateLabel(next), attendeesLabel(next)].filter(Boolean).join(' · ')}
                  </Typography>
                  <Typography variant="p3" color="secondary" component="span" className={styles.summaryCount}>
                    {upcomingCount}
                  </Typography>
                </span>
              </>
            ) : (
              <Typography variant="p2" color="secondary" component="span">
                {t('connectors.meetings.upcoming.empty')}
              </Typography>
            )}
          </Card>
        )}

        <Collapsible open={expanded} id={bodyId} innerClassName={styles.expandedBody}>
          <Typography variant="p2" color="secondary" className={styles.notice}>
            {emails ? t('connectors.meetings.connectedAs', { emails }) : t('connectors.meetings.calendarGone')}
          </Typography>

          {next && (
            <div className={styles.nextUp}>
              <Typography variant="p3" color="secondary" component="span" className={styles.nextUpLabel}>
                {`${t('connectors.meetings.nextUp')} · ${whenLabel(next)}`}
              </Typography>
              <ul className={styles.rows}>
                <MeetingRow
                  layout="card"
                  meeting={next}
                  stateLabel={stateLabel(next)}
                  busy={decide.isLoading}
                  opening={openingKey === meetingRowKey(next)}
                  onOpen={() => void openMeeting(next)}
                  onDecide={(decision) => onDecide(next, decision)}
                />
              </ul>
            </div>
          )}

          {/* The two tabs are two different things, and the caption beside them says which: the calendar's
              meetings that have not ended, or the meetings she was in. */}
          <div className={styles.tabsRow}>
            <Tabs<MeetingsTab>
              size="small"
              label={t('connectors.channel.meetings')}
              value={tab}
              onChange={(value) => {
                clicked(`meetings_tab_${value}`);
                setTab(value);
              }}
              items={[
                {
                  value: 'upcoming',
                  label: (
                    <>
                      {t('connectors.meetings.upcoming')}
                      {ahead > 0 && <span className={styles.tabCount}>{ahead}</span>}
                    </>
                  ),
                },
                { value: 'past', label: t('connectors.meetings.past') },
              ]}
            />
            <Typography variant="p3" color="secondary" component="span" className={styles.tabsCaption}>
              {tab === 'upcoming'
                ? t('connectors.meetings.upcoming.range')
                : t('connectors.meetings.past.caption', { agent: agentName ?? ownAgent.name })}
            </Typography>
          </div>

          {/* The history is read only once its tab is open, so a visit that never looks at it spends nothing. */}
          {tab === 'upcoming' ? (
            upcoming.length === 0 ? (
              <Typography variant="p2" color="secondary">
                {t('connectors.meetings.upcoming.empty')}
              </Typography>
            ) : (
              <MeetingList
                meetings={rest}
                now={now}
                locale={locale}
                stateLabel={stateLabel}
                busy={decide.isLoading}
                openingKey={openingKey}
                onOpen={(meeting) => void openMeeting(meeting)}
                onDecide={onDecide}
                onToggleAll={(showing) => clicked(showing ? 'meetings_show_less' : 'meetings_show_more')}
              />
            )
          ) : (
            <MeetingHistorySection now={now} />
          )}
          {openFailed && (
            <Typography variant="p3" color="secondary" component="p" role="alert">
              {t('connectors.meetings.error')}
            </Typography>
          )}
        </Collapsible>
        {calendarModal}
        {settingsOpen && (
          <MeetingsSettingsModal
            connection={connection}
            canTalkAvailable={canTalkAvailable}
            switchesLocked={switchesLocked}
            disconnecting={disconnect.isLoading}
            onClose={() => setSettingsOpen(false)}
            // Reconnect hands over to the calendar modal rather than stacking a second dialog on this one.
            onReconnect={() => {
              clicked('meetings_reconnect');
              setSettingsOpen(false);
              setCalendarModalOpen(true);
            }}
            // Closed with the connection: the dialog only renders while connected, so a left-open one would
            // reappear on the next connect.
            onDisconnect={() => {
              setSettingsOpen(false);
              clicked('meetings_disconnect');
              setCalendarJustConnected(false);
              disconnect.mutate();
            }}
            onAutoJoinChange={(on) => {
              clicked(on ? 'meetings_auto_join_on' : 'meetings_auto_join_off');
              update.mutate({ joinPolicy: on ? 'ALL_VIDEO_EVENTS' : 'REQUESTED_ONLY' });
            }}
            onCanTalkChange={(on) => {
              clicked(on ? 'meetings_can_talk_on' : 'meetings_can_talk_off');
              update.mutate({ canTalk: on });
            }}
          />
        )}
      </div>,
    ),
    trailing,
  );
}

/**
 * The rest of the week, after the Next up card: the first few rows under their day, then "Show N more"
 * in place. No nested scroll box, because a second scrollbar inside the card hides rows from anyone who
 * misses it.
 */
function MeetingList({
  meetings,
  now,
  locale,
  stateLabel,
  busy,
  openingKey,
  onOpen,
  onDecide,
  onToggleAll,
}: {
  meetings: UpcomingMeeting[];
  now: number;
  locale: string | undefined;
  stateLabel: (meeting: UpcomingMeeting) => string;
  busy: boolean;
  openingKey: string | null;
  onOpen: (meeting: UpcomingMeeting) => void;
  onDecide: (meeting: UpcomingMeeting, decision: 'join' | 'skip' | null) => void;
  onToggleAll: (showingAll: boolean) => void;
}) {
  const { t } = useTranslation();
  const [showAll, setShowAll] = useState(false);
  if (meetings.length === 0) return null;
  const hidden = meetings.length - VISIBLE_MEETINGS;
  const shown = showAll || hidden <= 0 ? meetings : meetings.slice(0, VISIBLE_MEETINGS);

  return (
    <div className={styles.upcoming}>
      {groupByDay(shown, (meeting) => Date.parse(meeting.startsAt)).map((group) => (
        <section key={group.day} className={styles.dayGroup}>
          <Typography variant="p3" color="secondary" component="h3" className={styles.dayHeading}>
            {meetingDayHeading(group.day, now, locale)}
          </Typography>
          <ul className={styles.dayRows}>
            {group.items.map((meeting) => (
              <MeetingRow
                key={meetingRowKey(meeting)}
                meeting={meeting}
                time={meetingClock(Date.parse(meeting.startsAt), locale)}
                stateLabel={stateLabel(meeting)}
                busy={busy}
                opening={openingKey === meetingRowKey(meeting)}
                onOpen={() => onOpen(meeting)}
                onDecide={(decision) => onDecide(meeting, decision)}
              />
            ))}
          </ul>
        </section>
      ))}
      {hidden > 0 && (
        <TextButton
          size="small"
          className={styles.showMore}
          onClick={() => {
            onToggleAll(showAll);
            setShowAll(!showAll);
          }}
        >
          {showAll ? t('connectors.meetings.showLess') : t('connectors.meetings.showMore', { count: hidden })}
        </TextButton>
      )}
    </div>
  );
}

/**
 * One meeting. As a list row, under its day: the start time, then the title over who is in it, then her
 * state and her one action at the end. As the Next up card (`layout="card"`, whose label above already
 * says when): the title, who is in it, then her state and the action on a line of their own.
 */
function MeetingRow({
  meeting,
  time,
  stateLabel,
  busy,
  opening,
  onOpen,
  onDecide,
  layout = 'row',
}: {
  meeting: UpcomingMeeting;
  time?: string;
  stateLabel: string;
  busy: boolean;
  opening: boolean;
  onOpen: () => void;
  onDecide: (decision: 'join' | 'skip' | null) => void;
  layout?: 'row' | 'card';
}) {
  const { t } = useTranslation();
  const action = meetingRowAction(meeting.state);
  const faces = meeting.participantNames ?? [];
  const badge = <Badge className={styles.rowState} variant={MEETING_STATE_BADGE[meeting.state]} label={stateLabel} />;
  const actionButton = action !== 'none' && (
    <Button variant="text" size="small" disabled={busy} className={styles.rowAction} onClick={() => onDecide(meetingRowDecision(action))}>
      {t(`connectors.meetings.action.${action}`)}
    </Button>
  );
  const people = meeting.participantCount > 0 && (
    <span className={styles.rowPeople}>
      {faces.length > 0 && (
        <span className={styles.faces} aria-hidden>
          {faces.map((name, index) => (
            <Avatar key={`${name}-${index}`} size={20} initials={attendeeInitials(name)} color={avatarSlotFor(name)} className={styles.face} />
          ))}
        </span>
      )}
      {meeting.participantCount === 1
        ? t('connectors.meetings.attendeesOne')
        : t('connectors.meetings.attendees', { count: meeting.participantCount })}
    </span>
  );
  const title = (
    <TextButton size="small" className={styles.rowTitle} disabled={opening} onClick={onOpen}>
      {meeting.title || t('connectors.meetings.untitled')}
    </TextButton>
  );

  if (layout === 'card') {
    return (
      <li className={styles.row}>
        {title}
        {people}
        <span className={styles.rowBottom}>
          {badge}
          {actionButton}
        </span>
      </li>
    );
  }

  return (
    <li className={styles.timedRow}>
      <span className={styles.rowTime}>{time}</span>
      <span className={styles.rowMain}>
        {title}
        {people}
      </span>
      <span className={styles.rowEnd}>
        {badge}
        {actionButton}
      </span>
    </li>
  );
}
